import type { ApiKeyScope, ApiKeyView, IssuedApiKey } from "@developerz.ai/ggcoin-sdk";
import { createSignal, For, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { formatExpiry } from "../lib/format.ts";

const SCOPES: readonly ApiKeyScope[] = [
  "wallet:read",
  "wallet:write",
  "transaction:write",
  "agent:provision",
];

export function ApiKeys() {
  const { client } = useAuth();
  const [keys, setKeys] = createSignal<ApiKeyView[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<unknown | null>(null);

  const [label, setLabel] = createSignal("");
  const [scopes, setScopes] = createSignal<Set<ApiKeyScope>>(new Set(["wallet:read"]));
  // Optional per-day request quota; blank = unlimited (#32).
  const [quota, setQuota] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  // The plaintext key is returned exactly once — surface it until dismissed.
  const [issued, setIssued] = createSignal<IssuedApiKey | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setKeys(await client.listApiKeys());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void refresh();
  });

  function toggleScope(scope: ApiKeyScope) {
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }

  async function onIssue(e: Event) {
    e.preventDefault();
    if (scopes().size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const q = quota().trim();
      const quotaPerDay = q ? Number(q) : undefined;
      if (quotaPerDay !== undefined && (!Number.isInteger(quotaPerDay) || quotaPerDay <= 0)) {
        setError(new Error("Quota/day must be a positive whole number (or blank for unlimited)."));
        setBusy(false);
        return;
      }
      const key = await client.issueApiKey({ label: label(), scopes: [...scopes()], quotaPerDay });
      setIssued(key);
      setLabel("");
      setScopes(new Set(["wallet:read"]));
      setQuota("");
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onSetQuota(keyId: string, current: number | null) {
    const raw = window.prompt(
      "Requests per day for this key (blank = unlimited):",
      current === null ? "" : String(current),
    );
    if (raw === null) return; // cancelled
    const trimmed = raw.trim();
    const quotaPerDay = trimmed === "" ? null : Number(trimmed);
    if (quotaPerDay !== null && (!Number.isInteger(quotaPerDay) || quotaPerDay <= 0)) {
      setError(new Error("Quota/day must be a positive whole number (or blank for unlimited)."));
      return;
    }
    setError(null);
    try {
      await client.updateApiKeyQuota(keyId, quotaPerDay);
      await refresh();
    } catch (err) {
      setError(err);
    }
  }

  async function onRevoke(keyId: string) {
    setError(null);
    try {
      await client.revokeApiKey(keyId);
      await refresh();
    } catch (err) {
      setError(err);
    }
  }

  async function onRotate(keyId: string) {
    setError(null);
    try {
      // The rotated key's plaintext shows once — same one-time callout as issuance.
      setIssued(await client.rotateApiKey(keyId));
      await refresh();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <section>
      <h1>API keys</h1>
      <p class="muted">Programmatic access for your integrations. Secrets are shown once.</p>
      <ErrorBanner error={error()} />

      <Show when={issued()}>
        {(key) => (
          <div class="callout" role="status">
            <strong>Copy your new key now — it won't be shown again.</strong>
            <pre class="secret">{key().key}</pre>
            <button type="button" class="ghost" onClick={() => setIssued(null)}>
              Done
            </button>
          </div>
        )}
      </Show>

      <form class="card form" onSubmit={onIssue}>
        <h3>Issue a key</h3>
        <label for="label">Label</label>
        <input
          id="label"
          value={label()}
          maxLength={80}
          placeholder="ci-pipeline"
          onInput={(e) => setLabel(e.currentTarget.value)}
          required
        />
        <span class="muted">Scopes</span>
        <div class="checks">
          <For each={SCOPES}>
            {(scope) => (
              <label class="check">
                <input
                  type="checkbox"
                  checked={scopes().has(scope)}
                  onChange={() => toggleScope(scope)}
                />
                {scope}
              </label>
            )}
          </For>
        </div>
        <label for="quota">Quota / day (optional)</label>
        <input
          id="quota"
          type="number"
          min="1"
          step="1"
          value={quota()}
          placeholder="unlimited"
          onInput={(e) => setQuota(e.currentTarget.value)}
        />
        <div class="row">
          <button type="submit" class="primary" disabled={busy() || scopes().size === 0}>
            {busy() ? "Issuing…" : "Issue key"}
          </button>
        </div>
      </form>

      <div class="table-scroll">
        <table class="table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Prefix</th>
              <th>Scopes</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Quota/day</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <Show
              when={!loading()}
              fallback={
                <tr>
                  <td colSpan={7} class="muted">
                    Loading…
                  </td>
                </tr>
              }
            >
              <Show
                when={keys().length > 0}
                fallback={
                  <tr>
                    <td colSpan={7} class="muted">
                      No API keys yet.
                    </td>
                  </tr>
                }
              >
                <For each={keys()}>
                  {(k) => (
                    <tr>
                      <td>{k.label}</td>
                      <td>
                        <code>{k.prefix}</code>
                      </td>
                      <td>{k.scopes.join(", ")}</td>
                      <td>
                        <span class={`badge badge-${k.status}`}>{k.status}</span>
                      </td>
                      <td>{formatExpiry(k.expiresAt)}</td>
                      <td class="num">{k.quotaPerDay ?? "∞"}</td>
                      <td class="actions">
                        <Show when={k.status === "active"}>
                          <button
                            type="button"
                            class="ghost"
                            onClick={() => onSetQuota(k.id, k.quotaPerDay)}
                          >
                            Quota
                          </button>
                          <button type="button" class="ghost" onClick={() => onRotate(k.id)}>
                            Rotate
                          </button>
                          <button type="button" class="ghost danger" onClick={() => onRevoke(k.id)}>
                            Revoke
                          </button>
                        </Show>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </Show>
          </tbody>
        </table>
      </div>
    </section>
  );
}
